import { Injectable, OnDestroy, inject } from "@angular/core";
import _ from "lodash";
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    forkJoin,
    of
} from "rxjs";
import {
    filter,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { RuleErrorContext, RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { HandelssortimentService } from "~app/features/forsaljning/handelssortiment/handelssortiment.service";
import { IMarknadsprislista } from "~app/features/virkeskop/models/marknadsprislista-model";
import { IMarknadsomrade } from "~app/features/virkeskop/models/verksamhetsomraden-models";
import { MarknadsprislistaService } from "~app/features/virkeskop/services/marknadsprislista.service";
import { UpdateKontraktUtskriftCommand } from "../commands/utskrift/UpdateKontraktUtskriftCommand";
import { VirkeskopskontraktPrisDTO } from "../dto/pristabell/pristabell-dtos";
import { AvverkningskostnaderEvent } from "../models/avverkningskostnader/avverkningskostnader-models";
import { Kontrakt } from "../models/kontrakt-models";
import { MarknadsprislistaFilter } from "../models/pristabell/marknadsprislistaFilter";
import { PristabellRad } from "../models/pristabell/pristabell-models";
import { CreateVirkeskopskontraktUtskriftRequestDTO, KontraktUtskriftValues, VKUtskriftValuesDTO, VirkeskopskontraktUtskriftDTO } from "../models/utskrift/utskriftValues-models";
import { Vardeberakning } from "../models/vardeberakning/vardeberakning-models";
import { VirkeskostnaderEvent } from "../models/virkeskostnader/virkeskostnader-models";
import { initPristabell, sortPristabell } from "../utils/pristabell/pristabell-utilities";
import { setDefaultUtskriftValues } from "../utils/utskrift/utskrift-utilities";
import { AvtalService } from "./avtal.service";
import { AvverkningskostnaderMediatorService } from "./avverkningskostnader-mediator.service";
import { ForkalkylService } from "./forkalkyl.service";
import { ForkalkylmallarService } from "./forkalkylmallar.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { VirkeskopValidationService } from "./validation/virkeskop-validation.service";
import { VirkeskostnaderMediatorService } from "./virkeskostnader-mediator.service";

@Injectable()
export class UtskriftMediatorService implements OnDestroy {
    readonly inkluderaBeloppIUtskrift$ = new BehaviorSubject(false);
    readonly marknadsomradeOptions$: Observable<IMarknadsomrade[]>;
    readonly marknadsprislistaFilter$ = new ReplaySubject<MarknadsprislistaFilter>(1);
    readonly mattslagOptions$: Observable<string[]>;
    readonly panelExpanded$ = new ReplaySubject<void>(1);
    readonly prislistaOptions$: Observable<IMarknadsprislista[]>;
    readonly vardekalkylClick$ = new BehaviorSubject(false);
    readonly vardekalkylChanged$: Observable<Vardeberakning>;
    private readonly avtalService = inject(AvtalService);
    private readonly forkalkylmallarService = inject(ForkalkylmallarService);
    private readonly avverkningskostnaderMediatorService = inject(AvverkningskostnaderMediatorService);
    private readonly destroy$ = new Subject<void>();
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly handelssortimentService = inject(HandelssortimentService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktsbeloppChangeSrc = new Subject<number>();
    private readonly marknadsprislistaService = inject(MarknadsprislistaService);
    private readonly resetSrc = new Subject<void>();
    private readonly utskrifValuesSrc = new BehaviorSubject<KontraktUtskriftValues>(null);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);

    constructor() {
        this.mattslagOptions$ = this.forkalkylmallarService
            .getVardenByGrupp("VK_MATTSLAG")
            .pipe(map(values => values.map(x => x.kod)),
                shareReplay(1));

        this.prislistaOptions$ = this.marknadsprislistaService.getMarknadsprislistor(null).pipe(
            shareReplay(1));

        this.marknadsomradeOptions$ = this.marknadsprislistaService.getMarknadsomrade().pipe(
            shareReplay(1));

        this.vardekalkylChanged$ = combineLatest([
            this.kontraktMediatorService.kontrakt$,
            this.virkeskostnaderMediatorService.virkeskostnader$,
            this.avverkningskostnaderMediatorService.avverkningskostnader$
        ]).pipe(
            filter((models) => models.every(x => !_.isNil(x))),
            switchMap((models) => this.requestDefaultUtskrift(models)),
            shareReplay(1)
        );
    }

    get inkluderaBeloppIUtskrift() { return this.inkluderaBeloppIUtskrift$.value; }

    get kontraktsbeloppChange$() { return this.kontraktsbeloppChangeSrc.asObservable(); }

    get reset$() { return this.resetSrc.asObservable(); }

    get utskriftValues$() { return this.utskrifValuesSrc.asObservable(); }

    getHandelssortiment(pristabell: PristabellRad[]) {
        const ids = pristabell.map(x => x.handelssortiment_Id).filter(x => x > 0);
        return this.handelssortimentService.searchForHandelssortiment({ ids });
    }

    initUtskriftValues(canEdit: boolean) {
        const initModel = (dto: VKUtskriftValuesDTO, kontrakt: Kontrakt) => {
            if (ObjectUtils.hasValue(dto)) {
                return this.createModel(dto.utskrift, kontrakt.id);
            }

            if (!canEdit) {
                return of(null);
            }

            // finns ingen sparad utskriftsentitet
            // väntar på att panelen expanderas och gör en implict återställning
            return this.panelExpanded$.pipe(
                take(1),
                switchMap(() => this.getDefaultUtskriftValues([], true)),
                tap(values => this.updateUtskriftValues(values.toDto())));
        };

        const kontrakt$ = this.kontraktMediatorService.kontrakt$;
        const utskrift$ = kontrakt$.pipe(
            switchMap(kontrakt => kontrakt?.id
                ? this.avtalService.getKontraktUtskriftValues(kontrakt.id)
                : of(null as VKUtskriftValuesDTO)),
            tap((dto) => this.displayValidationMessages(
                dto?.validationMessages
                ?? [])),
            withLatestFrom(kontrakt$),
            mergeMap(([
                dto,
                kontrakt
            ]) => initModel(dto, kontrakt))
        );

        utskrift$.pipe(
            filter(utskrift => !!utskrift),
            tap(utskrift => {
                this.utskrifValuesSrc.next(utskrift);
                this.kontraktsbeloppChangeSrc.next(utskrift.kontraktsbelopp);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    populatePristabell(currentPristabell: PristabellRad[]) {
        const pristabellDto = currentPristabell.map(x => x.toDto());
        const newUtskrift$ = this.getDefaultUtskriftValues(pristabellDto, false);

        combineLatest([
            this.utskrifValuesSrc,
            newUtskrift$
        ]).pipe(
            take(1)
        ).subscribe(([
            current,
            next
        ]) => {
            // använd pristabell från nya, men behåll övriga befintliga värden
            current.pristabell = next.pristabell;
            this.utskrifValuesSrc.next(current);
            this.updateUtskriftValues(current.toDto());
        });
    }

    resetUtskriftValues() {
        this.getDefaultUtskriftValues([], true).subscribe({
            next: (utskrift) => {
                this.utskrifValuesSrc.next(utskrift);
                this.updateUtskriftValues(utskrift.toDto());
                this.resetSrc.next();
            },
            error: console.error
        });
    }

    addPrisrad(prisrad: PristabellRad) {
        const current = this.utskrifValuesSrc.value;
        current.pristabell = sortPristabell([
            ...current.pristabell,
            prisrad
        ]);
        this.updateUtskriftValues(current.toDto());
    }

    removePrisrad(prisrad: PristabellRad) {
        const current = this.utskrifValuesSrc.value;
        current.pristabell = current.pristabell.filter(rad => rad !== prisrad);
        this.updateUtskriftValues(current.toDto());
    }

    updateUtskriftValues(model: VirkeskopskontraktUtskriftDTO) {
        this.utskrifValuesSrc.next(new KontraktUtskriftValues(model, this.kontraktMediatorService.kontrakt.id));
        this.kontraktsbeloppChangeSrc.next(model.kontraktsbelopp);
        const command = new UpdateKontraktUtskriftCommand(model, this.kontraktMediatorService.kontrakt.guid);
        this.kontraktCommandService.update(command);
    }

    private requestDefaultUtskrift(models: [Kontrakt, VirkeskostnaderEvent, AvverkningskostnaderEvent]) {
        const [
            kontrakt,
            virkeskostnaderEvent,
            avverkningskostnaderEvent
        ] = models;
        const vardeberakningRequest = this.forkalkylService.createVardeberakningRequest(
            kontrakt,
            virkeskostnaderEvent?.virkeskostnader,
            avverkningskostnaderEvent);

        return this.forkalkylService.vardeberakningForkalkyl(vardeberakningRequest);
    }

    private createModel(dto: VirkeskopskontraktUtskriftDTO, kontraktId: number, vb?: Vardeberakning, recalculateValues = true): Observable<KontraktUtskriftValues> {
        const utskrift = new KontraktUtskriftValues(dto, kontraktId);
        const useApris = this.kontraktMediatorService.kontrakt.isAffarsformU4 || this.kontraktMediatorService.kontrakt.isAffarsformU5;
        return this.getHandelssortiment(utskrift.pristabell).pipe(
            tap(hs => utskrift.pristabell = initPristabell(utskrift.pristabell, hs, vb, recalculateValues, useApris)),
            map(() => utskrift)
        );
    }

    private displayValidationMessages(messages: RuleValidationMessage[]) {
        this.validationService.clearClientSideContext(RuleErrorContext.rapportutskrift);
        messages.forEach(message => {
            this.validationService.updateClientSideValidation(true, message);
        });
    }

    private getDefaultUtskriftValues(currentPristabell: VirkeskopskontraktPrisDTO[], recalculateValues: boolean) {
        /*
        Hämta default utskrift + gör en värdeberäkning baserat på samma data parallelt
        Om recalculatevalues är false så räknas inte nya värden fram (sortimentsnetto/TA) etc på befintliga rader.
        */
        const requestDefaultUtskrift = (models: [Kontrakt, VirkeskostnaderEvent, AvverkningskostnaderEvent]) => {
            const [
                kontrakt,
                virkeskostnaderEvent,
                avverkningskostnaderEvent
            ] = models;
            const vardeberakningRequest = this.forkalkylService.createVardeberakningRequest(
                kontrakt,
                virkeskostnaderEvent?.virkeskostnader,
                avverkningskostnaderEvent);

            const utskriftOptions: CreateVirkeskopskontraktUtskriftRequestDTO = {
                affarsform: kontrakt.affarsform,
                nettotyp: kontrakt.nettotyp,
                skordarmatning: kontrakt.skordarmatning,
                vardeberakningRequest,
                currentPristabell
            };

            return forkJoin([
                this.avtalService.defaultUtskriftValues(kontrakt.id, utskriftOptions),
                this.forkalkylService.vardeberakningForkalkyl(vardeberakningRequest)
            ]);
        };

        const getAtgarder = () => this.kontraktMediatorService.atgarder;
        const getKontrakt = () => this.kontraktMediatorService.kontrakt;

        return combineLatest([
            this.kontraktMediatorService.kontrakt$,
            this.virkeskostnaderMediatorService.virkeskostnader$,
            this.avverkningskostnaderMediatorService.avverkningskostnader$
        ]).pipe(
            filter((models) => models.every(x => !_.isNil(x))),
            take(1),
            mergeMap((models) => requestDefaultUtskrift(models)),
            mergeMap(([
                dto,
                vardeberakning
            ]) => this.createModel(dto, dto.kontraktId, vardeberakning, recalculateValues)),
            tap((values) => setDefaultUtskriftValues(values, getKontrakt(), getAtgarder()))
        );
    }
}
